/**
 * Formatação de dados para o texto do documento.
 *
 * Regra da casa: no banco tudo é SÓ DÍGITO. A pontuação nasce aqui, uma única
 * vez, e por isso não pode divergir. Nas amostras o mesmo CEP aparece como
 * `86702-851` e `86702851`, e o mesmo CNAE como `14.12-6-01` e `1411-8/02`.
 */
const MESES = [
    'janeiro',
    'fevereiro',
    'março',
    'abril',
    'maio',
    'junho',
    'julho',
    'agosto',
    'setembro',
    'outubro',
    'novembro',
    'dezembro',
];
export function soDigitos(valor) {
    return valor.replace(/\D/g, '');
}
/** `02116539854` → `021.165.398-54`. */
export function formatarCpf(cpf) {
    const d = soDigitos(cpf);
    if (d.length !== 11)
        throw new RangeError(`CPF com ${d.length} dígitos: ${cpf}`);
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}
/** `33483404000132` → `33.483.404/0001-32`. */
export function formatarCnpj(cnpj) {
    const d = soDigitos(cnpj);
    if (d.length !== 14)
        throw new RangeError(`CNPJ com ${d.length} dígitos: ${cnpj}`);
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}
/** `86702851` → `86702-851`. Formato ÚNICO: sem ponto de milhar no CEP. */
export function formatarCep(cep) {
    const d = soDigitos(cep);
    if (d.length !== 8)
        throw new RangeError(`CEP com ${d.length} dígitos: ${cep}`);
    return `${d.slice(0, 5)}-${d.slice(5)}`;
}
/** `4930202` → `4930-2/02`. Formato da CONCLA, o mesmo que a Junta imprime. */
export function formatarCnae(cnae) {
    const d = soDigitos(cnae);
    if (d.length !== 7)
        throw new RangeError(`CNAE com ${d.length} dígitos: ${cnae}`);
    return `${d.slice(0, 4)}-${d.slice(4, 5)}/${d.slice(5)}`;
}
/** Centavos → `R$ 50.000,00`. */
export function formatarMoeda(centavos) {
    if (!Number.isInteger(centavos)) {
        throw new RangeError(`formatarMoeda espera centavos inteiros, recebeu ${centavos}`);
    }
    const negativo = centavos < 0;
    const absoluto = Math.abs(centavos);
    const reais = Math.floor(absoluto / 100);
    const resto = String(absoluto % 100).padStart(2, '0');
    const comPontos = String(reais).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${negativo ? '-' : ''}R$ ${comPontos},${resto}`;
}
/** Inteiro com separador de milhar: `50000` → `50.000`. */
export function formatarInteiro(n) {
    if (!Number.isInteger(n)) {
        throw new RangeError(`formatarInteiro espera inteiro, recebeu ${n}`);
    }
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}
/**
 * Percentual com duas casas: `33.333333` → `33,33`.
 *
 * Trunca em vez de arredondar de propósito: com três sócios em partes iguais,
 * arredondar faz a coluna somar 100,01 — e a Junta confere essa soma.
 * O resíduo é absorvido pelo maior sócio na montagem da tabela.
 */
export function formatarPercentual(p) {
    const truncado = Math.trunc(p * 100) / 100;
    return truncado.toFixed(2).replace('.', ',');
}
/** `2026-07-29` → `29/07/2026`. */
export function formatarData(iso) {
    const [ano, mes, dia] = iso.split('-');
    if (!ano || !mes || !dia)
        throw new RangeError(`data ISO inválida: ${iso}`);
    return `${dia}/${mes}/${ano}`;
}
/** `2026-07-29` → `29 de julho de 2026`. Fecho do documento e data do ato. */
export function dataPorExtenso(iso) {
    const [ano, mes, dia] = iso.split('-');
    if (!ano || !mes || !dia)
        throw new RangeError(`data ISO inválida: ${iso}`);
    const nomeMes = MESES[Number(mes) - 1];
    if (!nomeMes)
        throw new RangeError(`mês inválido em ${iso}`);
    return `${dia} de ${nomeMes} de ${ano}`;
}
/**
 * Endereço numa linha, na ordem que a Junta espera.
 *
 * Uma função só, usada em todo lugar: é o que impede o mesmo endereço sair
 * como "Loja A1" numa cláusula e "Sala A1" na consolidação, como em
 * `docs/amostras/primeira-alteracao-slu.docx`.
 */
export function enderecoEmLinha(e) {
    const partes = [
        `${e.logradouro}, nº ${e.numero}`,
        e.complemento,
        `Bairro ${e.bairro}`,
        `CEP ${formatarCep(e.cep)}`,
        `${e.municipio}/${e.uf}`,
    ].filter((p) => Boolean(p && p.trim()));
    return partes.join(', ');
}
//# sourceMappingURL=formato.js.map